import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './PayRoll.css';
import { Layout } from '@/layout/Layout';
import {
  createNewPayRoll,
  getAllPayrolls,
  updatePayroll,
  deletePayroll
} from '@/services/HrService/PayrollService';
import { Visibility, Edit, Delete } from '@mui/icons-material';
import { useAppTheme } from '@contexts/ThemeContext';

/* ================= TYPES ================= */

interface PayrollPeriod {
  id: number;
  payroll_name: string;
  start_date: string;
  end_date: string;
  pay_date: string;
  status: 'draft' | 'approved' | 'sent';
  created_at?: string;
  updated_at?: string;
}

interface SnackbarState {
  message: string;
  type: 'success' | 'error';
}

/* ================= COMPONENT ================= */

const Payroll: React.FC = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useAppTheme();
  const [payrollPeriods, setPayrollPeriods] = useState<PayrollPeriod[]>([]);
  const [filteredPayrolls, setFilteredPayrolls] = useState<PayrollPeriod[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<SnackbarState | null>(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    open: boolean;
    payrollId: number | null;
  }>({
    open: false,
    payrollId: null,
  });

  const [formData, setFormData] = useState<{
    payroll_name: string;
    payPeriodStart: string;
    payPeriodEnd: string;
    payDay: string;
    status?: 'draft' | 'approved' | 'sent';
  }>({
    payroll_name: '',
    payPeriodStart: '',
    payPeriodEnd: '',
    payDay: '',
    status: 'draft',
  });

  /* ================= EFFECTS ================= */

  useEffect(() => {
    fetchPayrolls();
  }, []);

  // Filter and search effect
  useEffect(() => {
    let filtered = [...payrollPeriods];

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (payroll) =>
          payroll.payroll_name.toLowerCase().includes(search) ||
          payroll.status.toLowerCase().includes(search)
      );
    }

    setFilteredPayrolls(filtered);
    setCurrentPage(1); // Reset to first page when search changes
  }, [payrollPeriods, searchTerm]);

  /* ================= API CALLS ================= */

  const fetchPayrolls = async () => {
    setFetchLoading(true);
    try {
      const { success, data, error } = await getAllPayrolls({
        page: 1,
        limit: 100
      });

      if (!success || !data) {
        throw new Error(error?.message || 'Failed to fetch payrolls');
      }

      setPayrollPeriods(data);
    } catch (err: any) {
      setSnackbar({
        message: err.message || 'Failed to load payrolls',
        type: 'error',
      });
    } finally {
      setFetchLoading(false);
    }
  };

  /* ================= HANDLERS ================= */

  const handleOpenCreateModal = () => {
    setIsEditMode(false);
    setEditingId(null);
    setFormData({
      payroll_name: '',
      payPeriodStart: '',
      payPeriodEnd: '',
      payDay: '',
      status: 'draft',
    });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (period: PayrollPeriod) => {
    setIsEditMode(true);
    setEditingId(period.id);

    // Format dates to YYYY-MM-DD for input type="date"
    const formatDateForInput = (dateStr: string) => {
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    setFormData({
      payroll_name: period.payroll_name,
      payPeriodStart: formatDateForInput(period.start_date),
      payPeriodEnd: formatDateForInput(period.end_date),
      payDay: formatDateForInput(period.pay_date),
      status: period.status,
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (isEditMode) {
      await handleUpdatePeriod();
    } else {
      await handleCreatePeriod();
    }
  };

  const handleCreatePeriod = async () => {
    if (
      !formData.payroll_name ||
      !formData.payPeriodStart ||
      !formData.payPeriodEnd ||
      !formData.payDay
    ) {
      setSnackbar({ message: 'Please fill all required fields', type: 'error' });
      return;
    }

    setLoading(true);

    try {
      const { success, data, error } = await createNewPayRoll({
        payroll_name: formData.payroll_name.trim(),
        start_date: formData.payPeriodStart,
        end_date: formData.payPeriodEnd,
        pay_date: formData.payDay,
        status: formData.status || 'draft',
      });

      if (!success || !data) {
        throw new Error(error?.message || 'Failed to create new payroll');
      }

      setPayrollPeriods((prev) => [...prev, data]);
      setIsModalOpen(false);
      setFormData({
        payroll_name: '',
        payPeriodStart: '',
        payPeriodEnd: '',
        payDay: '',
        status: 'draft',
      });
      setSnackbar({ message: 'Payroll created successfully!', type: 'success' });
    } catch (err: any) {
      setSnackbar({
        message: err.message || 'Something went wrong',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePeriod = async () => {
    if (
      !formData.payroll_name ||
      !formData.payPeriodStart ||
      !formData.payPeriodEnd ||
      !formData.payDay
    ) {
      setSnackbar({ message: 'Please fill all required fields', type: 'error' });
      return;
    }

    if (!editingId) return;

    setLoading(true);

    try {
      const { success, data, error } = await updatePayroll(editingId, {
        payroll_name: formData.payroll_name.trim(),
        start_date: formData.payPeriodStart,
        end_date: formData.payPeriodEnd,
        pay_date: formData.payDay,
        status: formData.status,
      });

      if (!success || !data) {
        throw new Error(error?.message || 'Failed to update payroll');
      }

      setPayrollPeriods((prev) =>
        prev.map((period) => (period.id === editingId ? data : period))
      );
      setIsModalOpen(false);
      setFormData({
        payroll_name: '',
        payPeriodStart: '',
        payPeriodEnd: '',
        payDay: '',
        status: 'draft',
      });
      setIsEditMode(false);
      setEditingId(null);
      setSnackbar({ message: 'Payroll updated successfully!', type: 'success' });
    } catch (err: any) {
      setSnackbar({
        message: err.message || 'Something went wrong',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteConfirm = (id: number) => {
    setDeleteConfirmDialog({
      open: true,
      payrollId: id,
    });
  };

  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmDialog({
      open: false,
      payrollId: null,
    });
  };

  const handleConfirmDelete = async () => {
    const id = deleteConfirmDialog.payrollId;
    if (!id) return;

    handleCloseDeleteConfirm();

    try {
      const { success, error } = await deletePayroll(id);

      if (!success) {
        throw new Error(error?.message || 'Failed to delete payroll');
      }

      setPayrollPeriods((prev) => prev.filter((period) => period.id !== id));
      setSnackbar({ message: 'Payroll deleted successfully!', type: 'success' });
    } catch (err: any) {
      setSnackbar({
        message: err.message || 'Failed to delete payroll',
        type: 'error',
      });
    }
  };

  const handleViewPayroll = (id: number) => {
    navigate(`/payroll/${id}`);
  };

  const closeSnackbar = () => setSnackbar(null);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPayrolls.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPayrolls.length / itemsPerPage);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  /* ================= RENDER ================= */

  return (
    <Layout>
      <div className="payroll-container" data-theme={isDarkMode ? 'dark' : 'light'}>
        <div className="payroll-header">
          <div>
            <h1>Payroll Management</h1>
            <p className="subtitle">Create and manage payrolls</p>
          </div>
          <button className="btn-primary" onClick={handleOpenCreateModal}>
            <span>+</span> Create New Payroll
          </button>
        </div>

        {/* Search Bar */}
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div style={{ padding: '1rem' }}>
            <input
              type="text"
              placeholder="Search by payroll name or status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                fontSize: '1rem',
                border: '1px solid #ccc',
                borderRadius: '4px',
              }}
            />
          </div>
        </div>

        <div className="card">
          {fetchLoading ? (
            <div className="loading-state">Loading payrolls...</div>
          ) : (
            <>
              <table className="payroll-table">
                <thead>
                  <tr>
                    <th>Payroll Name</th>
                    <th>Pay Start Date</th>
                    <th>Pay End Date</th>
                    <th>Pay Date</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="empty-state">
                        {searchTerm ? 'No payrolls match your search.' : 'No payroll found. Create your first payroll !'}
                      </td>
                    </tr>
                  ) : (
                    currentItems.map((period) => (
                      <tr key={period.id} className="table-row-hover">
                        <td>
                          <strong>{period.payroll_name}</strong>
                        </td>
                        <td>{formatDate(period.start_date)}</td>
                        <td>{formatDate(period.end_date)}</td>
                        <td>{formatDate(period.pay_date)}</td>
                        <td>
                          <span
                            className={`chip chip-${
                              period.status === 'draft'
                                ? 'warning'
                                : period.status === 'approved'
                                ? 'success'
                                : 'info'
                            }`}
                          >
                            {period.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="actions">
                          <button
                            className="icon-btn"
                            title="View Details"
                            onClick={() => handleViewPayroll(period.id)}
                          >
                            <Visibility style={{ fontSize: '1.2rem' }} />
                          </button>
                          <button
                            className="icon-btn"
                            title="Edit"
                            onClick={() => handleOpenEditModal(period)}
                          >
                            <Edit style={{ fontSize: '1.2rem' }} />
                          </button>
                          <button
                            className="icon-btn delete"
                            title="Delete"
                            onClick={() => handleOpenDeleteConfirm(period.id)}
                          >
                            <Delete style={{ fontSize: '1.2rem' }} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              {filteredPayrolls.length > 0 && totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  padding: '1rem',
                  borderTop: '1px solid #eee',
                }}>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      style={{
                        padding: '0.5rem 1rem',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        background: currentPage === 1 ? '#f5f5f5' : '#fff',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        fontSize: '0.875rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                      }}
                    >
                      ‹ Previous
                    </button>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 1rem',
                      minWidth: '100px',
                      justifyContent: 'center',
                    }}>
                      <span style={{ fontSize: '0.875rem' }}>
                        Page {currentPage} of {totalPages}
                      </span>
                    </div>
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      style={{
                        padding: '0.5rem 1rem',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        background: currentPage === totalPages ? '#f5f5f5' : '#fff',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        fontSize: '0.875rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                      }}
                    >
                      Next ›
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* MODAL */}
        {isModalOpen && (
          <div className="modal-backdrop" onClick={() => setIsModalOpen(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h2 className="modal-title">
                {isEditMode ? 'Edit Payroll' : 'Create Payroll'}
              </h2>

              <div className="modal-body">
                <div className="form-group">
                  <label>Payroll Name *</label>
                  <input
                    type="text"
                    placeholder="e.g., December 2025"
                    value={formData.payroll_name}
                    onChange={(e) =>
                      setFormData({ ...formData, payroll_name: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Pay Start Date *</label>
                  <input
                    type="date"
                    value={formData.payPeriodStart}
                    onChange={(e) =>
                      setFormData({ ...formData, payPeriodStart: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Pay End Date *</label>
                  <input
                    type="date"
                    value={formData.payPeriodEnd}
                    onChange={(e) =>
                      setFormData({ ...formData, payPeriodEnd: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Pay Day *</label>
                  <input
                    type="date"
                    value={formData.payDay}
                    onChange={(e) =>
                      setFormData({ ...formData, payDay: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Status *</label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value as 'draft' | 'approved' | 'sent' })
                    }
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '1rem',
                    }}
                  >
                    <option value="draft">Draft</option>
                    <option value="approved">Approved</option>
                    <option value="sent">Sent</option>
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn" onClick={() => setIsModalOpen(false)} disabled={loading}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={handleSubmit} disabled={loading}>
                  {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Payroll' : 'Create Payroll')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* SNACKBAR */}
        {snackbar && (
          <div className={`snackbar ${snackbar.type} show`}>
            {snackbar.message}
            <button className="snackbar-close" onClick={closeSnackbar}>
              ×
            </button>
          </div>
        )}

        {/* DELETE CONFIRMATION MODAL */}
        {deleteConfirmDialog.open && (
          <div className="modal-backdrop" onClick={handleCloseDeleteConfirm}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
              <h2 className="modal-title">Confirm Delete</h2>
              <div className="modal-body">
                <p style={{ fontSize: '1rem', marginBottom: '1.5rem' }}>
                  Are you sure you want to delete this payroll?
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn" onClick={handleCloseDeleteConfirm}>
                  No
                </button>
                <button className="btn-primary" onClick={handleConfirmDelete} style={{ backgroundColor: '#f44336' }}>
                  Yes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Payroll;
